import React from "react";
import { ChevronLeft, ChevronDown } from "lucide-react";
import { ResponsiveData } from "@/types/models/page";
import {
  ComponentConfig,
  ComponentDefinition,
} from "@/types/models/componentConfig";
import { DeviceType } from "@/services/sectionDataService";
import { DynamicInput } from "../../ui/elements/DynamicInput";

interface NestedPanelProps {
  componentKey: string | null;
  componentData?: ResponsiveData;
  componentDefinitions: ComponentDefinition[];
  onBack: () => void;
  formValues: Record<string, unknown>;
  onInputChange: (fieldKey: string, value: unknown) => void;
  currentDevice: DeviceType;
  onDeviceChange: (newDevice: DeviceType) => void;
  isLocked?: boolean;
  layoutMode?: "grid" | "vertical";
  showDeviceTabs?: boolean;
}

const NestedPanel: React.FC<NestedPanelProps> = ({
  componentKey,
  componentData,
  componentDefinitions,
  onBack,
  formValues,
  onInputChange,
  currentDevice,
  onDeviceChange,
  isLocked = false,
  layoutMode = "vertical",
  showDeviceTabs = true,
}) => {
  const showContent = componentKey && componentData;

  // Prefer backend definitions; fall back to inferring from componentData
  const componentDef =
    componentDefinitions.find(
      (def) => def.slug === componentData?.componentSlug,
    ) ||
    (componentData
      ? {
          id: `${componentData.componentSlug || "unknown"}-inline` as string,
          name:
            componentData.name || componentData.componentSlug || "Component",
          slug: componentData.componentSlug || "unknown",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          config: (Array.isArray(componentData.desktop)
            ? componentData.desktop
            : []
          ).map((input: any) => ({
            title: input?.inputKey || "unknown",
            description: `${input?.inputKey || "field"} configuration`,
            type: input?.type || "text",
            accepted_values: null,
            accepted_range: null,
          })) as ComponentConfig[],
        }
      : undefined);

  return (
    <div
      className="flex flex-col h-full"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {showContent ? (
        <>
          <div className="flex items-center p-4 border-b border-border dark:border-auth-background">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onBack();
              }}
              className="mr-2 p-1 rounded-full hover:bg-muted"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h3 className="font-semibold">{componentData.name}</h3>
              <p className="text-xs text-muted-foreground">
                {componentData.description}
              </p>
            </div>
          </div>

          {isLocked && (
            <div className="p-4 border-b border-border dark:border-auth-background">
              <div className="flex items-center space-x-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <ChevronDown className="w-4 h-4 text-amber-600" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Section is locked. Unlock it to edit properties.
                </p>
              </div>
            </div>
          )}

          {showDeviceTabs && (
            <div className="p-4 border-b border-border dark:border-auth-background">
              <div className="flex space-x-1 bg-muted dark:bg-auth-background p-1 rounded-lg">
                {(["desktop", "tablet", "mobile"] as DeviceType[]).map(
                  (device) => (
                    <button
                      key={device}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isLocked) {
                          onDeviceChange(device);
                        }
                      }}
                      disabled={isLocked}
                      className={`flex-1 py-2 px-3 text-xs font-medium rounded-md transition-colors ${
                        isLocked
                          ? "opacity-50 cursor-not-allowed"
                          : currentDevice === device
                            ? "bg-primary text-primary-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {device.charAt(0).toUpperCase() + device.slice(1)}
                    </button>
                  ),
                )}
              </div>
            </div>
          )}

          <div
            className={`flex-1 p-4 overflow-y-auto ${isLocked ? "pointer-events-none opacity-50" : ""}`}
          >
            {componentDef ? (
              <div
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {layoutMode === "grid" ? (
                  <div className="grid grid-cols-3 gap-4">
                    {componentDef.config.map((config) => {
                      const fieldKey = `${componentDef.slug}::${config.title}`;
                      const currentValue = formValues[fieldKey];
                      const type = (config.type || "").toLowerCase();
                      const wrapperClass =
                        type.includes("wysiwyg") ||
                        type.includes("richtext") ||
                        type.includes("textarea")
                          ? "col-span-3"
                          : type === "text" ||
                              type === "number" ||
                              type === "select" ||
                              type === "url"
                            ? "col-span-1"
                            : "col-span-2";
                      return (
                        <div key={fieldKey} className={wrapperClass}>
                          <DynamicInput
                            config={config}
                            value={currentValue}
                            onChange={(value) => {
                              if (!isLocked) {
                                onInputChange(fieldKey, value);
                              }
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {componentDef.config.map((config) => {
                      // Use '::' as separator to match sectionDataService
                      const fieldKey = `${componentDef.slug}::${config.title}`;
                      const currentValue = formValues[fieldKey];
                      return (
                        <DynamicInput
                          key={fieldKey}
                          config={config}
                          value={currentValue}
                          onChange={(value) => {
                            if (!isLocked) {
                              onInputChange(fieldKey, value);
                            }
                          }}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No configuration found for this component.
              </p>
            )}
          </div>
        </>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground text-sm">
            No component selected.
          </p>
        </div>
      )}
    </div>
  );
};

export default NestedPanel;
